import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { JobCardStatus, JobCardConfig } from '@/app/types/jobCard';
import {
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
  Loader2,
} from 'lucide-react';

interface StatusTimelineProps {
  currentStatus: JobCardStatus;
  config: JobCardConfig;
  onStatusChange: (newStatus: JobCardStatus, notes?: string) => Promise<void>;
  isUpdating?: boolean;
  disabled?: boolean;
}

// Define the visual order of statuses
const statusOrder: JobCardStatus[] = [
  'arrival',
  'inspection',
  'quoted',
  'approved',
  'under_service',
  'quality_check',
  'ready',
  'delivered',
  'invoiced',
];

// Status colors for pills
const statusColors: Record<JobCardStatus, { bg: string; border: string; text: string; ring: string }> = {
  arrival: { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-700', ring: 'ring-slate-400' },
  inspection: { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-700', ring: 'ring-cyan-400' },
  quoted: { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-700', ring: 'ring-yellow-400' },
  approved: { bg: 'bg-lime-100', border: 'border-lime-400', text: 'text-lime-700', ring: 'ring-lime-400' },
  under_service: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700', ring: 'ring-blue-400' },
  quality_check: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700', ring: 'ring-purple-400' },
  ready: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-700', ring: 'ring-emerald-400' },
  delivered: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700', ring: 'ring-green-400' },
  invoiced: { bg: 'bg-teal-100', border: 'border-teal-400', text: 'text-teal-700', ring: 'ring-teal-400' },
  cancelled: { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-700', ring: 'ring-red-400' },
};

export const StatusTimeline: React.FC<StatusTimelineProps> = ({
  currentStatus,
  config,
  onStatusChange,
  isUpdating = false,
  disabled = false,
}) => {
  const [clickedStatus, setClickedStatus] = useState<JobCardStatus | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<JobCardStatus | null>(null);
  const [transitionDialogOpen, setTransitionDialogOpen] = useState(false);
  const [transitionNotes, setTransitionNotes] = useState('');

  // Get allowed transitions for current status
  const allowedTransitions = config.status_transitions[currentStatus] || [];

  // Get status label from config
  const getStatusLabel = (status: JobCardStatus): string => {
    const statusConfig = config.statuses.find((s) => s.code === status);
    return statusConfig?.name || status.replace('_', ' ');
  };

  // Check if status is before current in workflow
  const isCompleted = (status: JobCardStatus): boolean => {
    const currentIndex = statusOrder.indexOf(currentStatus);
    const statusIndex = statusOrder.indexOf(status);
    return statusIndex < currentIndex;
  };

  // Check if status is current
  const isCurrent = (status: JobCardStatus): boolean => {
    return status === currentStatus;
  };

  // Check if status can be transitioned to
  const canTransition = (status: JobCardStatus): boolean => {
    return allowedTransitions.includes(status);
  };

  // Terminal states disable all pills
  const isTerminal = currentStatus === 'invoiced' || currentStatus === 'cancelled';

  // Check if transition is to the next sequential status (one step forward)
  const isNextSequentialStatus = (targetStatus: JobCardStatus): boolean => {
    const currentIndex = statusOrder.indexOf(currentStatus);
    const targetIndex = statusOrder.indexOf(targetStatus);
    return targetIndex === currentIndex + 1;
  };

  // Handle pill click
  const handlePillClick = async (status: JobCardStatus): Promise<void> => {
    if (disabled || isUpdating || isTerminal || !canTransition(status)) return;

    // Cancel is destructive/terminal -- requires confirmation
    if (status === 'cancelled') {
      setCancelDialogOpen(true);
      return;
    }

    // Next sequential status: advance instantly (minimize friction for common case)
    if (isNextSequentialStatus(status)) {
      setClickedStatus(status);
      try {
        await onStatusChange(status);
      } finally {
        setClickedStatus(null);
      }
      return;
    }

    // Non-sequential status jump: open dialog with optional notes
    setPendingStatus(status);
    setTransitionNotes('');
    setTransitionDialogOpen(true);
  };

  // Handle transition dialog confirm
  const handleTransitionConfirm = async (): Promise<void> => {
    if (!pendingStatus) return;
    const status = pendingStatus;
    const notes = transitionNotes.trim() || undefined;
    setClickedStatus(status);
    setTransitionDialogOpen(false);
    setPendingStatus(null);
    setTransitionNotes('');
    try {
      await onStatusChange(status, notes);
    } finally {
      setClickedStatus(null);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = async (): Promise<void> => {
    setClickedStatus('cancelled');
    setCancelDialogOpen(false);
    try {
      await onStatusChange('cancelled');
    } finally {
      setClickedStatus(null);
    }
  };

  // Get status icon
  const getStatusIcon = (status: JobCardStatus): React.ReactNode => {
    // Show spinner on the pill being updated
    if ((isUpdating || clickedStatus === status) && clickedStatus === status) {
      return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    }
    if (isCompleted(status)) return <CheckCircle2 className="h-3.5 w-3.5" />;
    if (isCurrent(status)) return <Clock className="h-3.5 w-3.5" />;
    return <Circle className="h-3.5 w-3.5" />;
  };

  // Check if cancel is an allowed transition
  const canCancel = allowedTransitions.includes('cancelled');

  return (
    <div className="space-y-3">
      {/* Status pills */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {statusOrder.map((status) => {
          const colors = statusColors[status];
          const completed = isCompleted(status);
          const current = isCurrent(status);
          const clickable = canTransition(status) && !disabled && !isUpdating && !isTerminal;

          return (
            <button
              key={status}
              type="button"
              disabled={!clickable}
              onClick={() => handlePillClick(status)}
              className={cn(
                'inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-2 sm:px-3 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium border transition-all touch-manipulation',
                // Completed statuses: green check styling
                completed && 'bg-green-50 border-green-300 text-green-700',
                // Current status: bold ring + colored background
                current && cn(
                  colors.bg,
                  colors.text,
                  'border-2',
                  colors.border,
                  'ring-2 ring-offset-1',
                  colors.ring,
                  'font-bold',
                ),
                // Future statuses: outlined/lighter
                !completed && !current && cn(
                  'bg-gray-50 border-gray-200 text-gray-400',
                ),
                // Clickable states
                clickable && 'cursor-pointer hover:shadow-md active:scale-95',
                clickable && !completed && !current && 'hover:bg-gray-100 hover:border-gray-300 hover:text-gray-600',
                clickable && completed && 'hover:bg-green-100',
                // Disabled states
                !clickable && 'cursor-default opacity-70',
              )}
            >
              {getStatusIcon(status)}
              <span>{getStatusLabel(status)}</span>
            </button>
          );
        })}

        {/* Cancel pill -- only if cancel is an allowed transition */}
        {canCancel && !isTerminal && (
          <button
            type="button"
            disabled={disabled || isUpdating}
            onClick={() => handlePillClick('cancelled')}
            className={cn(
              'inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-2 sm:px-3 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium border transition-all touch-manipulation',
              'bg-red-50 border-red-300 text-red-600',
              'cursor-pointer hover:bg-red-100 hover:shadow-md active:scale-95',
              (disabled || isUpdating) && 'cursor-default opacity-70',
            )}
          >
            {clickedStatus === 'cancelled' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <XCircle className="h-3.5 w-3.5" />
            )}
            <span>Cancel</span>
          </button>
        )}
      </div>

      {/* Cancelled status banner */}
      {currentStatus === 'cancelled' && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <XCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-700 font-medium">This job card has been cancelled</span>
        </div>
      )}

      {/* Cancel confirmation dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Job Card?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the job card. This action cannot be easily undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Cancel Job Card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Non-sequential transition dialog with optional notes */}
      <AlertDialog open={transitionDialogOpen} onOpenChange={(open) => {
        setTransitionDialogOpen(open);
        if (!open) {
          setPendingStatus(null);
          setTransitionNotes('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move to {pendingStatus ? getStatusLabel(pendingStatus) : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will skip intermediate statuses and move directly to{' '}
              {pendingStatus ? getStatusLabel(pendingStatus) : ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Add notes (optional)..."
            value={transitionNotes}
            onChange={(e) => setTransitionNotes(e.target.value)}
            className="min-h-[80px]"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleTransitionConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
